<template>
  <div>
    <b-button variant="outline-primary" class="ml-u8 mb-u20" @click="goToPreviousPage">
      <span class="mr-2"> <font-awesome-icon icon="arrow-left" /> </span>
      Back to previous page
    </b-button>

    <b-container>
      <b-row class="justify-content-center">
        <b-col cols="10" md="6" lg="4">
          <div class="h2 font-weight-bold text-center mt-u8">Edit Profile</div>
          <div class="d-flex justify-content-center container__img-profile">
            <b-spinner v-show="isLoading" small />
            <BaseAvatar
              v-show="!isLoading"
              :name="`${user.firstname} ${user.lastname}`"
              :img="profileImageSrc"
            />
            <div class="container__upload-img">
              <b-button class="button__upload-img rounded-circle" @click="openModal()">
                <font-awesome-icon
                  class="icon__upload-img"
                  :icon="{ prefix: 'fas', iconName: 'camera' }"
                />
              </b-button>
            </div>
          </div>

          <!-- Name -->
          <div class="mt-u8">
            <label for="input__name" class="mb-u4"> Name </label>
            <b-form-input
              id="input__name"
              v-model="form.firstname"
              class="input__name"
              placeholder="Enter your name"
              :disabled="true"
            />
          </div>

          <!-- Surname -->
          <div class="mt-u4">
            <label for="input__surname" class="mb-u4"> Surname </label>
            <b-form-input
              id="input__surname"
              v-model="form.lastname"
              class="input__surname"
              placeholder="Enter your surname"
              :disabled="true"
            />
          </div>

          <!-- Email -->
          <div class="mt-u4">
            <label for="input__email" class="mb-u4"> Email </label>
            <b-form-input
              id="input__email"
              v-model="form.email"
              class="input__email"
              placeholder="Enter your email"
              :disabled="true"
            />
          </div>

          <!-- Description -->
          <div class="mt-u4">
            <label for="input__description" class="mb-u4"> Description </label>
            <b-form-textarea
              id="input__description"
              v-model="form.description"
              class="input__description"
              placeholder="Enter your description"
              :disabled="false"
            />
          </div>

          <!-- Button -->
          <div class="d-flex mt-u15 mb-u10">
            <b-button variant="success" style="width: 76px" :disabled="isSaving" @click="save">
              <b-spinner v-if="isSaving" style="width: 1rem; height: 1rem" class="" />
              <div v-else>Save</div>
            </b-button>
            <b-button variant="link" class="ml-u1" @click="goToPreviousPage"> Cancel </b-button>
          </div>
        </b-col>
      </b-row>
    </b-container>

    <ModalUploadProfile
      :file.sync="file.profile_picture"
      :original-file.sync="file.original_profile_picture"
      :user="user"
      :show.sync="modalShow"
      :reference-meta="{ ref: 'user', refId: user.id }"
      @upload:success="onUploadSuccess"
      @deleted:success="onDeletedSuccess"
    />
  </div>
</template>

<script>
import { userInfoMixin } from '@/mixins/userInfoMixin';
import { notificationMixin } from '@/mixins/notificationMixin';
import BaseAvatar from '@/components/base-components/BaseAvatar.vue';
import User from '@/models/User';
import Upload from '@/models/Upload';
import ModalUploadProfile from './components/ModalUploadProfile.vue';
import { faCamera } from '@fortawesome/free-solid-svg-icons';
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

library.add(faCamera);

export default {
  components: {
    BaseAvatar,
    FontAwesomeIcon,
    ModalUploadProfile
  },
  mixins: [userInfoMixin, notificationMixin],

  data() {
    return {
      form: {
        firstname: '',
        lastname: '',
        email: '',
        description: '',
        base64Profile_picture: null,
        profile_picture: null
      },
      isSaving: false,
      modalShow: false,
      isLoading: false,
      file: {
        profile_picture: [],
        original_profile_picture: []
      },
      profileImageSrc: null
    };
  },

  created() {
    this.fetchProfileImageSrc();
    this.init();
  },

  methods: {
    async init() {
      const { firstname, lastname, email, description, profile_picture, original_profile_picture } =
        this.user;

      this.form = {
        firstname,
        lastname,
        email,
        description
      };

      if (profile_picture) {
        this.$set(this.file.profile_picture, 0, profile_picture);
      }

      if (original_profile_picture) {
        this.$set(this.file.original_profile_picture, 0, original_profile_picture);
      }
    },

    async save() {
      this.isSaving = true;
      try {
        await User.api().updateUser(this.form);
        this.notiSuccessDefault('update profile success.');
      } catch (error) {
        console.log('error', error);
        this.notiErrorDefault('something went wrong');
        console.error(error);
      }
      this.isSaving = false;
      return this.$router.push({ name: 'profile' });
    },

    goToPreviousPage() {
      this.$router.go(-1);
    },
    openModal() {
      this.modalShow = true;
    },

    async onUploadSuccess({ file = null, originalFile = null }) {
      this.isLoading = true;

      if (file && originalFile) {
        this.isLoading = true;

        this.$set(this.file.profile_picture, 0, {
          url: file.dataURL ?? file.url,
          id: file.id
        });

        this.$set(this.file.original_profile_picture, 0, {
          url: originalFile.dataURL ?? originalFile.url,
          id: originalFile.id
        });

        await User.update({
          where: this.user.id,
          data: {
            profile_picture: file,
            original_profile_picture: originalFile
          }
        });
      } else {
        this.$toast.error('Something went wrong.');
      }

      this.fetchProfileImageSrc();

      this.isLoading = false;
    },
    onDeletedSuccess() {
      this.profileImageSrc = null;

      this.init();
    },
    async fetchProfileImageSrc() {
      const { profile_picture } = this.user;

      const { response } = await Upload.api().fetchPresignedUrl(profile_picture.id);

      this.profileImageSrc = response.data.url;
    }
  }
};
</script>

<style scoped>
.input__description {
  height: 160px;
}
.container__img-profile {
  position: relative;
}
.container__upload-img {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
.icon__upload-img {
  font-size: 20px;
  color: #ffffff;
  z-index: 0;
  background-color: rgba(0, 0, 0, 0.42);
  padding: 10px;
  border-radius: 50%;
  cursor: pointer;
}
.button__upload-img {
  width: 100px;
  height: 100px;
  background-color: transparent;
  border: none;
  outline: none;
  box-shadow: none;
  box-shadow: none !important;
  cursor: pointer;
}
.button__upload-img:hover {
  box-shadow: none !important;
}
.button__upload-img:active {
  background-color: transparent !important;
}
</style>
